import React from 'react'
import cn from 'classnames'
import {
  Button,
  IconAddDefault,
  IconCheckDefault,
  Separator,
} from '@mc/design-system'
import { Trans, useTranslation, withI18nContext } from '@mc/i18n'
import { TrackingContainer } from '@mc/visibility'
import { SkeletonLoader } from '../../../../components/SkeletonLoader'
import CertificateBadge from '../../assets/certificate-badge.svg'
import { useCertsAddOn } from '../../context/certsAddOnContext'
import {
  CERTS_ADD_ON_SURFACE,
  trackCertsAddOnViewed,
} from '../../events/certsAddOnViewed'
import { CERTS_QUIZ_PLANS_I18N_NAMESPACE, i18nInstance } from '../../i18n'
import { FAQ_UUID } from '../CertsAddOnFAQ'
import classes from './CertificatesAddOnCard.module.scss'

// Both figures resolve from the same request, so both are stood in for. Sized
// to the bullet's own type (13px at a 1.45 line height) so the sentence keeps
// its shape and nothing below it moves when the prices arrive.
const FIGURE_SKELETON_HEIGHT = 13
const PRICE_SKELETON_WIDTH = 46
const DISCOUNT_SKELETON_WIDTH = 26

// Stands in for one figure inside the sentence. `SkeletonLoader` renders no
// children, so the figure `Trans` passes in is replaced rather than wrapped.
const FigureSkeleton = ({ width }: { width: number }) => (
  <SkeletonLoader
    className={`d-inline-block ${classes.figureSkeleton}`}
    height={FIGURE_SKELETON_HEIGHT}
    width={width}
  />
)

const openCertsAddOnFaq = () => {
  if (typeof window === 'undefined') return

  const heading = window.document.querySelector<HTMLElement>(
    `[data-uuid="${FAQ_UUID}"]`,
  )
  if (!heading) return

  const scrollToHeading = () => heading.scrollIntoView({ behavior: 'smooth' })

  const button = heading.querySelector<HTMLButtonElement>(
    'button[aria-expanded="false"]',
  )
  if (button) {
    button.focus({ preventScroll: true })
    button.click()
    // Wait for the Framer Motion accordion expansion (default 200ms) to
    // finish; otherwise the in-flight animation interrupts the smooth scroll.
    window.setTimeout(scrollToHeading, 250)
  } else {
    scrollToHeading()
  }
}

export const CertificatesAddOnCard = withI18nContext(i18nInstance)(() => {
  const { t } = useTranslation(CERTS_QUIZ_PLANS_I18N_NAMESPACE)
  const { isCardVisible, isAdded, isLoading, discount, monthlyPrice, toggle } =
    useCertsAddOn()

  if (!isCardVisible) return null

  return (
    <TrackingContainer
      className='mc-w-100 row no-gutters d-flex justify-content-center'
      onFirstVisible={() => trackCertsAddOnViewed(CERTS_ADD_ON_SURFACE.CARD)}
    >
      <div className={cn(classes.column, 'col-12 ')}>
        <div
          className={cn(classes.card, 'mc-corners--md mc-p-4 mc-mb-6 mc-mx-4')}
          data-testid='certs-add-on-card'
        >
          <div className='row no-gutters d-flex align-items-center justify-content-between mc-gap-8'>
            <div className='col-auto d-flex align-items-center mc-gap-3'>
              <CertificateBadge
                aria-hidden='true'
                style={{ flexShrink: 0, height: '28px', width: '24px' }}
              />
              <span className='mc-text-h6'>{t('certsAddOn.title')}</span>
            </div>
            <div className='col col-md-3'>
              <Button
                fullWidth
                kind={isAdded ? 'primary' : 'tertiary'}
                size='sm'
                aria-pressed={isAdded}
                onClick={toggle}
              >
                {isAdded ? (
                  <IconCheckDefault
                    className='mc-mr-2'
                    width={16}
                    height={16}
                  />
                ) : (
                  <IconAddDefault className='mc-mr-2' width={16} height={16} />
                )}
                {isAdded ? t('certsAddOn.added') : t('certsAddOn.add')}
              </Button>
            </div>
          </div>
          <Separator className='mc-my-5' />
          <ul
            className={`${classes.benefits} mc-text-color--tint mc-m-0 mc-pl-5`}
          >
            <li>{t('certsAddOn.pitch')}</li>
            <li>
              <Trans
                i18nKey={'certsAddOn.price'}
                ns={CERTS_QUIZ_PLANS_I18N_NAMESPACE}
                values={{ price: monthlyPrice, discount }}
                components={{
                  prices: isLoading ? (
                    <FigureSkeleton width={PRICE_SKELETON_WIDTH} />
                  ) : (
                    <span className='mc-text--bold mc-text-color--light' />
                  ),
                  discount: isLoading ? (
                    <FigureSkeleton width={DISCOUNT_SKELETON_WIDTH} />
                  ) : (
                    <span />
                  ),
                }}
              />
            </li>
            <li>
              <Trans
                i18nKey='certsAddOn.credit'
                ns={CERTS_QUIZ_PLANS_I18N_NAMESPACE}
                components={{
                  learnMore: (
                    <button
                      type='button'
                      className='mc-text--link'
                      onClick={openCertsAddOnFaq}
                    />
                  ),
                }}
              />
            </li>
          </ul>
        </div>
      </div>
    </TrackingContainer>
  )
})
